// Simplified Lorraine Ward boundary map.
//
// The boundary polygon is LCR's own published geometry for the unit, and
// lives in Salt Lake City grid coordinates (east of Main St, south of South
// Temple, in "address units": 100 per block, ~8 ft each). Household lat/lng from the
// geocode sync is projected into grid space with a linear fit calibrated on
// the ward's own geocoded addresses (see docs in the leq repo's
// refresh_geocodes tool); at neighborhood scale the error is well under a
// house lot. SVG user space IS grid space: x = east, y = south.
//
// Two renderers share that geometry:
//   WardMap      — one pin, used by the person and household detail pages.
//   WardMapMulti — N status-coded pins for an incident board or printed sheet.
// They differ in how they frame the view. A single pin may stretch the frame
// to stay visible; a board must NOT, because stretching to chase one bad
// geocode distorts every other pin. See frameFor.

const LNG0 = -111.891235;
const LNG_PER_EAST = 0.000028694;
const LAT0 = 40.772572;
const LAT_PER_SOUTH = 0.000022033;

export function gridFromLatLng(lat: number, lng: number): { east: number; south: number } {
  return { east: (lng - LNG0) / LNG_PER_EAST, south: (LAT0 - lat) / LAT_PER_SOUTH };
}

/** Inverse of gridFromLatLng — lets the board turn a click into coordinates.
 *  Addresses added during a response are never geocoded (the sync matches on
 *  lcr_uuid, and the Lambdas have no internet egress), so placing a pin by
 *  hand is the only way a non-roster neighbor lands on the map — and it works
 *  with no connectivity at all. */
export function latLngFromGrid(east: number, south: number): { lat: number; lng: number } {
  return { lat: LAT0 - south * LAT_PER_SOUTH, lng: LNG0 + east * LNG_PER_EAST };
}

// The ward boundary, in grid units, clockwise from the north-east corner.
//
// Not hand-drawn: this is LCR's published geometry for unit 4278, pulled from
// the ward directory map and pinned to the county's surveyed parcel corners.
// It runs along real lot lines (44.6% of its length) and down street
// centrelines (53.4%); the 1.9% that is neither crosses the large commercial
// parcels by Highland Drive, where there is no lot line to follow.
//
// Regenerate with `python -m tools.refresh_ward_boundary` — do not hand-edit,
// and do not re-trace it off the map image. The predecessor was traced by eye
// and sat a median 17 ft, and as much as 125 ft, off the lines it was meant
// to follow, which is what made PlatMap draw the boundary over the parcels so
// a lot line would not visibly break it.
export const BOUNDARY: [number, number][] = [
  [1411.13, 3296.9], [1402.98, 3297.22], [1338.18, 3298.35], [1295.83, 3297.62],
  [1281.18, 3297.82], [1259.32, 3298.08], [1225.35, 3298.55], [1214.26, 3298.28],
  [1196.42, 3297.82], [1186.6, 3297.62], [1178.36, 3297.42], [1158.78, 3297.42],
  [1119.32, 3297.62], [1098.73, 3298.28], [1063.84, 3299.34], [993.62, 3298.69],
  [951, 3298.69], [900.81, 3299.01], [846.3, 3299.47], [819.02, 3299.31],
  [798.36, 3299.3], [730.38, 3300.01], [696.22, 3299.44], [693.83, 3299.47],
  [666.34, 3299.04], [498.18, 3299.28], [497.7, 3299.28], [497.7, 3299.38],
  [465.21, 3299.61], [434.59, 3299.61], [397.98, 3299.54], [397.91, 3299.54],
  [397.71, 3339.67], [397.92, 3339.67], [397.68, 3375.39], [381.96, 3375.89],
  [381.93, 3393.32], [397.64, 3393.27], [397.61, 3399.08], [399.91, 3399.08],
  [399.88, 3411.16], [399.51, 3447.12], [318.88, 3447.06], [318.85, 3453.28],
  [320.1, 3453.28], [319.75, 3508.78], [323.76, 3508.78], [323.69, 3518.54],
  [359.59, 3518.59], [356.87, 3498.75], [372.13, 3498.8], [396.29, 3498.8],
  [396.15, 3518.49], [454.28, 3518.36], [454.28, 3527.3], [497.88, 3527.26],
  [497.62, 3590.18], [540.57, 3590.16], [540.57, 3594.29], [579.93, 3594.34],
  [581.45, 3594.34], [581.45, 3573.59], [642.92, 3573.5], [642.96, 3565.7],
  [678.98, 3565.34], [688.17, 3565.34], [693.3, 3565.16], [693.3, 3539.56],
  [692.77, 3515.32], [692.66, 3504.4], [693.44, 3458.44], [702.05, 3458.34],
  [740.87, 3458.22], [791.86, 3458.21], [841.15, 3458.54], [894.6, 3457.69],
  [897.38, 3457.9], [897.54, 3437.28], [923.01, 3437.25], [922.95, 3446.28],
  [1035.41, 3446.15], [1035.48, 3426.68], [1044.99, 3426.68], [1044.98, 3410.19],
  [1057.5, 3409.75], [1075.85, 3409.67], [1075.87, 3446.1], [1070.71, 3446.15],
  [1070.68, 3454.5], [1065.76, 3454.5], [1065.73, 3460.76], [1066.81, 3460.76],
  [1066.81, 3471.52], [1055.17, 3472.47], [1055.2, 3482.05], [1092.53, 3482],
  [1100.82, 3483.5], [1111.14, 3483.5], [1111.1, 3485.36], [1113.51, 3485.36],
  [1113.51, 3489.13], [1122.46, 3489.09], [1122.43, 3496.3], [1125.78, 3496.26],
  [1152.26, 3496.16], [1171.08, 3496.12], [1196.35, 3496.03], [1196.31, 3506.06],
  [1206.11, 3506.15], [1272.11, 3506.47], [1272.23, 3490.44], [1282.52, 3490.39],
  [1295.07, 3489.94], [1296.49, 3489.88], [1296.54, 3479.68], [1296.75, 3445.62],
  [1296.88, 3433.92], [1296.95, 3424.35], [1296.89, 3410.29], [1297.01, 3392.26],
  [1297.1, 3376.87], [1300.86, 3376.25], [1368.51, 3375.03], [1367.45, 3334.31],
  [1379.94, 3334.34], [1393.22, 3334], [1393.22, 3319.79], [1401.3, 3320.6],
  [1405.77, 3319.94], [1410.89, 3318.52], [1417.31, 3318.69], [1414.21, 3308.07],
];

const BOUNDARY_PATH = BOUNDARY.map(([e, s], i) => `${i ? 'L' : 'M'}${e} ${s}`).join(' ') + ' Z';

const MEETINGHOUSE = { east: 1077, south: 3400 };

// Reference streets that make the shape readable: 3300 S across the top and
// the numbered E verticals the boundary mostly follows.
const STREETS_E = [500, 700, 900, 1100, 1300];

// Base frame around the ward (grid units), and how far it may stretch to
// keep an out-of-boundary pin on the map before we give up and omit the pin.
const FRAME = { x0: 275, y0: 3262, x1: 1455, y1: 3630 };
const MAX_STRETCH = 300;

export interface Frame { x0: number; y0: number; x1: number; y1: number }

/** Frame the view around a set of pins.
 *
 *  With `stretch` (the single-pin case) the frame grows up to MAX_STRETCH to
 *  keep an out-of-boundary pin visible. Without it the frame stays fixed —
 *  which is what a board needs, since one mis-geocoded household must not
 *  rescale the other four hundred. */
export function frameFor(
  pins: { east: number; south: number }[],
  { stretch = false, pad = 35 }: { stretch?: boolean; pad?: number } = {},
): Frame {
  if (!stretch || pins.length === 0) return { ...FRAME };
  const east = pins.map((p) => p.east);
  const south = pins.map((p) => p.south);
  return {
    x0: Math.max(FRAME.x0 - MAX_STRETCH, Math.min(FRAME.x0, Math.min(...east) - pad)),
    y0: Math.max(FRAME.y0 - MAX_STRETCH, Math.min(FRAME.y0, Math.min(...south) - pad)),
    x1: Math.min(FRAME.x1 + MAX_STRETCH, Math.max(FRAME.x1, Math.max(...east) + pad)),
    y1: Math.min(FRAME.y1 + MAX_STRETCH, Math.max(FRAME.y1, Math.max(...south) + pad)),
  };
}

function withinFrame(p: { east: number; south: number }, f: Frame, inset = 10): boolean {
  return p.east >= f.x0 + inset && p.east <= f.x1 - inset
      && p.south >= f.y0 + inset && p.south <= f.y1 - inset;
}

export function isInsideWard(lat: number, lng: number): boolean {
  const { east, south } = gridFromLatLng(lat, lng);
  let c = false;
  for (let i = 0, j = BOUNDARY.length - 1; i < BOUNDARY.length; j = i++) {
    const [xi, yi] = BOUNDARY[i];
    const [xj, yj] = BOUNDARY[j];
    if (yi > south !== yj > south && east < ((xj - xi) * (south - yi)) / (yj - yi) + xi) {
      c = !c;
    }
  }
  return c;
}

const LABEL = { fontSize: 28, letterSpacing: 1 } as const;

function MapChrome({ frame }: { frame: Frame }) {
  const { x0, y0, x1, y1 } = frame;
  return (
    <>
      {/* reference streets */}
      <g className="text-slate-200 dark:text-slate-700" stroke="currentColor" strokeWidth={2.5} vectorEffect="non-scaling-stroke">
        <line x1={x0} y1={3300} x2={x1} y2={3300} vectorEffect="non-scaling-stroke" />
        {STREETS_E.map((e) => (
          <line key={e} x1={e} y1={y0} x2={e} y2={y1} vectorEffect="non-scaling-stroke" />
        ))}
      </g>
      <g className="fill-slate-400 dark:fill-slate-500" style={LABEL}>
        <text x={x0 + 18} y={3300 - 12}>E 3300 S</text>
        {STREETS_E.map((e) => (
          <text key={e} x={e + 10} y={y1 - 12}>{e} E</text>
        ))}
      </g>

      {/* ward boundary */}
      <path
        d={BOUNDARY_PATH}
        className="text-slate-500 dark:text-slate-400"
        fill="currentColor"
        fillOpacity={0.06}
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* meetinghouse */}
      <path
        className="fill-slate-400 dark:fill-slate-500"
        d={`M${MEETINGHOUSE.east} ${MEETINGHOUSE.south - 23}
            l 18 30 h -10 v 16 h -16 v -16 h -10 Z`}
      />
    </>
  );
}

/** The original single-pin marker: halo, ring, dot. */
function SoloPin({ east, south }: { east: number; south: number }) {
  return (
    <g className="text-sky-600 dark:text-sky-400">
      <circle cx={east} cy={south} r={80} fill="currentColor" opacity={0.15} />
      <circle
        cx={east}
        cy={south}
        r={38}
        fill="none"
        stroke="currentColor"
        strokeWidth={5}
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={east} cy={south} r={15} fill="currentColor" />
    </g>
  );
}

export function WardMap({ lat, lng }: { lat: number; lng: number }) {
  const pin = gridFromLatLng(lat, lng);
  const frame = frameFor([pin], { stretch: true });
  const { x0, y0, x1, y1 } = frame;
  const onMap = withinFrame(pin, frame);

  return (
    <svg
      viewBox={`${x0} ${y0} ${x1 - x0} ${y1 - y0}`}
      className="block w-full"
      role="img"
      aria-label="Simplified ward map showing this household's location relative to the Lorraine Ward boundary"
    >
      <MapChrome frame={frame} />
      {onMap && <SoloPin east={pin.east} south={pin.south} />}
    </svg>
  );
}

/** `inBoundary` is the caller's word on whether the household is in the ward,
 *  and wins over the pin. Half the ward's geocodes are grid points on the road
 *  centreline, and on a boundary street (1300 E) the centreline is a few feet
 *  outside the line, so the pin alone flags the houses on the inside frontage.
 *  The sector is the authority — it is what the coverage count uses. */
export function WardMapCard({ lat, lng, inBoundary }: {
  lat: number | null; lng: number | null; inBoundary?: boolean;
}) {
  if (lat == null || lng == null) return null;
  const inside = inBoundary ?? isInsideWard(lat, lng);
  const { east, south } = gridFromLatLng(lat, lng);
  const farOff =
    east < FRAME.x0 - MAX_STRETCH || east > FRAME.x1 + MAX_STRETCH ||
    south < FRAME.y0 - MAX_STRETCH || south > FRAME.y1 + MAX_STRETCH;

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-3 py-1">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Location</span>
        {!inside && (
          <span className="rounded bg-amber-100 dark:bg-amber-900/50 px-1.5 py-0.5 text-xs text-amber-800 dark:text-amber-300">
            Outside boundary
          </span>
        )}
      </div>
      <div className="px-2 pt-2 pb-1">
        <WardMap lat={lat} lng={lng} />
      </div>
      <div className="flex items-baseline justify-between px-3 pb-1.5 text-xs text-slate-400 dark:text-slate-500">
        <span>Lorraine Ward boundary</span>
        <span>{farOff ? 'Location is beyond this map' : '▲ meetinghouse'}</span>
      </div>
    </div>
  );
}

// ─── multi-pin ─────────────────────────────────────────────────────────────

/** Status is encoded by SHAPE as well as colour. The packet prints on a mono
 *  laser and some readers cannot separate red from green, so hue is always
 *  reinforcement rather than the only channel. */
export type PinShape = 'filled' | 'hollow' | 'square' | 'triangle';

export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  shape: PinShape;
  /** Tailwind text-* class; `fill`/`stroke` inherit from it via currentColor. */
  tone?: string;
  /** One or two characters drawn beside the mark, for packet cross-reference. */
  label?: string;
}

const PIN_R = 14;

function Pin({ east, south, shape, tone, label, scale }: {
  east: number; south: number; shape: PinShape;
  tone?: string; label?: string; scale: number;
}) {
  const r = PIN_R * scale;
  const common = { stroke: 'currentColor', strokeWidth: 3, vectorEffect: 'non-scaling-stroke' as const };
  return (
    <g className={tone ?? 'text-slate-500 dark:text-slate-400'}>
      {shape === 'filled' && <circle cx={east} cy={south} r={r} fill="currentColor" {...common} />}
      {shape === 'hollow' && <circle cx={east} cy={south} r={r} fill="none" {...common} />}
      {shape === 'square' && (
        <rect x={east - r} y={south - r} width={r * 2} height={r * 2} fill="currentColor" {...common} />
      )}
      {shape === 'triangle' && (
        <path d={`M${east} ${south - r * 1.2} L${east + r * 1.1} ${south + r * 0.9} L${east - r * 1.1} ${south + r * 0.9} Z`}
              fill="currentColor" {...common} />
      )}
      {label && (
        <text
          x={east + r * 1.6} y={south + r * 0.7}
          className="fill-slate-600 dark:fill-slate-300"
          style={{ fontSize: 26 * scale, letterSpacing: 0.5 }}
        >
          {label}
        </text>
      )}
    </g>
  );
}

export interface WardMapMultiProps {
  pins: MapPin[];
  /** Override the framing, e.g. to zoom a printed sheet onto one sector. */
  focus?: Frame;
  /** Click-to-place. Receives the clicked point in lat/lng. */
  onPlace?: (coords: { lat: number; lng: number }) => void;
  className?: string;
  /** Multiplier on marker size — the packet renders larger than the screen. */
  scale?: number;
}

/** Plot N pins on the ward.
 *
 *  Pins outside the frame are NOT drawn and NOT silently dropped: the count is
 *  returned to the caller through `onOffMap` so the UI can say "3 locations not
 *  shown". A map that quietly omits a household is worse than no map, because
 *  the operator reads absence as "already handled". */
export function WardMapMulti({
  pins, focus, onPlace, className, scale = 1,
  onOffMap,
}: WardMapMultiProps & { onOffMap?: (n: number) => void }) {
  const frame = focus ?? frameFor([], { stretch: false });
  const { x0, y0, x1, y1 } = frame;

  const placed = pins
    .map((p) => ({ ...p, ...gridFromLatLng(p.lat, p.lng) }))
    .filter((p) => withinFrame(p, frame, 0));
  onOffMap?.(pins.length - placed.length);

  const handleClick = onPlace
    ? (e: React.MouseEvent<SVGSVGElement>) => {
        const svg = e.currentTarget;
        const rect = svg.getBoundingClientRect();
        const east = x0 + ((e.clientX - rect.left) / rect.width) * (x1 - x0);
        const south = y0 + ((e.clientY - rect.top) / rect.height) * (y1 - y0);
        onPlace(latLngFromGrid(east, south));
      }
    : undefined;

  return (
    <svg
      viewBox={`${x0} ${y0} ${x1 - x0} ${y1 - y0}`}
      className={`block w-full ${onPlace ? 'cursor-crosshair' : ''} ${className ?? ''}`}
      onClick={handleClick}
      role="img"
      aria-label={`Ward map showing ${placed.length} location${placed.length === 1 ? '' : 's'}`}
    >
      <MapChrome frame={frame} />
      {placed.map((p) => (
        <Pin key={p.id} east={p.east} south={p.south} shape={p.shape}
             tone={p.tone} label={p.label} scale={scale} />
      ))}
    </svg>
  );
}
